import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Sparkles, MessageCircle, Star, Gift, ChevronRight, User, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { GemIcon } from '../components/GemIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  0: '#71717A',
  1: '#A1A1AA',
  2: '#60A5FA',
  3: '#34D399',
  4: '#FBBF24',
  5: '#FB923C',
  6: '#F87171',
  7: '#F472B6',
  8: '#C084FC',
  9: '#A78BFA',
  10: '#E11D48',
};

export default function CreatorCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creatorStats, setCreatorStats] = useState(null);
  const [gemsData, setGemsData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [myCharCount, setMyCharCount] = useState(0);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/creator/stats`, { withCredentials: true }).then(r => setCreatorStats(r.data)).catch(() => {}),
      axios.get(`${API_URL}/api/user/gems`, { withCredentials: true }).then(r => setGemsData(r.data)).catch(() => {}),
      axios.get(`${API_URL}/api/user/subscription`, { withCredentials: true }).then(r => setSubscription(r.data)).catch(() => {}),
      axios.get(`${API_URL}/api/user/me/characters`, { withCredentials: true }).then(r => setMyCharCount((r.data || []).length)).catch(() => {}),
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-24" data-testid="creator-center-page">
      <div
        className="sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-md border-b border-white/5 px-4 pb-3 flex items-center gap-3"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <button
          onClick={() => navigate('/profile')}
          className="p-2 -ml-2 text-white/70 hover:text-white rounded-full hover:bg-white/5"
          data-testid="creator-center-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-black text-lg text-white tracking-tight font-['Unbounded']">Creator Center</h1>
      </div>

      <div className="px-4 pt-4">
        {/* Stats card */}
        <div className="bg-[#1A1D24] rounded-2xl border border-white/5 overflow-hidden" data-testid="creator-stats-panel">
          <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-[#E11D48]/10 rounded-lg">
                <Sparkles className="w-4 h-4 text-[#E11D48]" />
              </div>
              <h3 className="text-sm font-bold text-white tracking-tight">Your Stats</h3>
            </div>
            {creatorStats && creatorStats.level > 0 && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${LEVEL_COLORS[creatorStats.level]}20`, color: LEVEL_COLORS[creatorStats.level] }}
                data-testid="stats-level-badge"
              >
                LVL {creatorStats.level} · {creatorStats.level_name}
              </span>
            )}
          </div>

          {creatorStats && (
            <div className="px-4 py-3 border-b border-white/5">
              <div className="flex items-end justify-between mb-1.5">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-black text-white font-['Unbounded'] leading-none" data-testid="stats-level-num">
                    {creatorStats.level}
                  </span>
                  <span className="text-xs text-[#71717A] font-medium">
                    {creatorStats.level >= 10 ? 'MAX LEVEL' : `→ Level ${creatorStats.level + 1}`}
                  </span>
                </div>
                {creatorStats.level < 10 && (
                  <span className="text-[10px] text-[#71717A] font-medium">
                    {creatorStats.progress_current} / {creatorStats.progress_max} chats
                  </span>
                )}
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${Math.min(100, (creatorStats.progress_current / Math.max(1, creatorStats.progress_max)) * 100)}%`,
                    backgroundColor: LEVEL_COLORS[creatorStats.level] || '#E11D48',
                    boxShadow: `0 0 12px ${LEVEL_COLORS[creatorStats.level] || '#E11D48'}80`,
                  }}
                  data-testid="stats-level-progress"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-px bg-white/5">
            <div className="bg-[#1A1D24] px-3 py-3 flex flex-col items-start" data-testid="stat-characters">
              <div className="flex items-center gap-1 text-[10px] text-[#71717A] font-bold tracking-wider uppercase mb-0.5">
                <Sparkles className="w-3 h-3" />
                Characters
              </div>
              <span className="text-xl font-black text-white font-['Unbounded'] leading-none">
                {creatorStats?.total_characters ?? myCharCount}
              </span>
            </div>
            <div className="bg-[#1A1D24] px-3 py-3 flex flex-col items-start" data-testid="stat-chats">
              <div className="flex items-center gap-1 text-[10px] text-[#71717A] font-bold tracking-wider uppercase mb-0.5">
                <MessageCircle className="w-3 h-3" />
                Total Chats
              </div>
              <span className="text-xl font-black text-white font-['Unbounded'] leading-none">
                {(creatorStats?.total_chats || 0).toLocaleString()}
              </span>
            </div>
            <div className="bg-[#1A1D24] px-3 py-3 flex flex-col items-start" data-testid="stat-rating">
              <div className="flex items-center gap-1 text-[10px] text-[#71717A] font-bold tracking-wider uppercase mb-0.5">
                <Star className="w-3 h-3" />
                Avg Rating
              </div>
              <span className="text-xl font-black text-white font-['Unbounded'] leading-none">
                {creatorStats?.avg_rating ? creatorStats.avg_rating.toFixed(1) : '—'}
              </span>
            </div>
            <div className="bg-[#1A1D24] px-3 py-3 flex flex-col items-start" data-testid="stat-followers">
              <div className="flex items-center gap-1 text-[10px] text-[#71717A] font-bold tracking-wider uppercase mb-0.5">
                <User className="w-3 h-3" />
                Followers
              </div>
              <span className="text-xl font-black text-white font-['Unbounded'] leading-none">
                {(user?.followers_count || 0).toLocaleString()}
              </span>
            </div>
            <div className="bg-[#1A1D24] px-3 py-3 flex flex-col items-start" data-testid="stat-gems">
              <div className="flex items-center gap-1 text-[10px] text-[#71717A] font-bold tracking-wider uppercase mb-0.5">
                <GemIcon size={12} />
                Gems
              </div>
              <span className="text-xl font-black text-[#F59E0B] font-['Unbounded'] leading-none">
                {(gemsData?.gems ?? user?.gems ?? 0).toLocaleString()}
              </span>
            </div>
            <div className="bg-[#1A1D24] px-3 py-3 flex flex-col items-start" data-testid="stat-tier">
              <div className="flex items-center gap-1 text-[10px] text-[#71717A] font-bold tracking-wider uppercase mb-0.5">
                <Crown className="w-3 h-3" />
                Tier
              </div>
              <span className="text-xs font-black text-white font-['Unbounded'] leading-none uppercase pt-1">
                {(subscription?.tier || user?.tier || 'free').replace(/^./, c => c.toUpperCase())}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between px-4 py-2.5 bg-white/[0.02] border-t border-white/5">
            <div className="flex items-center gap-1.5 text-[11px] text-[#71717A]">
              <Gift className="w-3.5 h-3.5 text-[#F59E0B]" />
              <span>
                <span className="text-[#F59E0B] font-bold">{(creatorStats?.total_gems_earned || 0).toLocaleString()}</span> gems earned from level rewards
              </span>
            </div>
            <Link
              to="/create"
              className="text-[11px] font-bold text-[#E11D48] hover:text-[#F43F5E] inline-flex items-center gap-0.5 transition-colors"
              data-testid="stats-create-link"
            >
              Create <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* Quick links */}
        <div className="mt-4 space-y-2">
          <Link
            to="/leaderboard"
            className="flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-yellow-500/10 via-fuchsia-500/10 to-orange-500/10 border border-white/5 hover:border-white/15 transition-all group"
            data-testid="creator-center-leaderboard"
          >
            <div className="w-9 h-9 rounded-xl bg-yellow-500/15 flex items-center justify-center">
              <Star className="w-4 h-4 text-yellow-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white leading-none">Top Creators</p>
              <p className="text-[10px] text-white/50 mt-0.5">Most chats · highest rated · top streaks</p>
            </div>
            <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
          </Link>

          <Link
            to="/create"
            className="flex items-center gap-3 p-3 rounded-2xl bg-[#1A1D24] border border-white/5 hover:border-white/15 transition-all group"
            data-testid="creator-center-create"
          >
            <div className="w-9 h-9 rounded-xl bg-[#E11D48]/15 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#E11D48]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white leading-none">Create a Character</p>
              <p className="text-[10px] text-white/50 mt-0.5">Build your own AI persona</p>
            </div>
            <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
          </Link>
        </div>
      </div>
    </div>
  );
}
